import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Avatar,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import StarIcon from '@mui/icons-material/Star';
import InfoIcon from '@mui/icons-material/Info';
import { PricingPlan } from '../../pages/Pricing';
import { HtmlRenderer } from '../HtmlRenderer';
import CrossedText from '../CrossedText';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Tab {
  label: string;
}

interface Feature {
  title: string;
  description: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const tabs: Tab[] = [
  { label: 'Lot Coverage' },
  { label: 'WebRail' },
  { label: 'DealerDash' },
  { label: 'Dealer Advisor' },
];

const featuresLeft: Feature[] = [
  {
    title: 'Lot Coverage',
    description:
      'QR Buddy tracks and reports every click once customer scans QR code',
  },
  {
    title: 'After Hour Coverage',
    description:
      'Captures lead information long after your lot has closed for the night',
  },
  {
    title: 'Web Rail',
    description:
      'Web widget records every click and reports to sales rep and dealer',
  },
  {
    title: 'Dealer Dash',
    description:
      'Smart reports, lead details, sales rep management, analyze vehicle popularity',
  },
  {
    title: 'Dealer Advisor',
    description:
      "Birds eye view of what's working, what's not working, and how to fix it",
  },
  {
    title: 'Advisor AI Integration',
    description: 'AI instantly answers your questions based on data collected',
  },
  {
    title: 'Sales Rep Dash',
    description:
      'View leads and their interests in real time, move through pipeline',
  },
  {
    title: 'Lead Notification',
    description:
      'Sales rep instantly notified via text when a lead has been received',
  },
  {
    title: 'Inventory Management',
    description: 'FTP uploads to QR Buddy ensure inventory is kept up to date',
  },
];

const featuresRight: Feature[] = [
  {
    title: 'Lead Disposition',
    description:
      'Choose between round robin, manual, blast, and AI assignment of leads',
  },
  {
    title: 'Google Reviews',
    description: 'Insantly connect your shoppers with your Google Reviews page',
  },
  {
    title: 'Ghost Leads',
    description:
      'QR Buddy begins collecting data before shopper becomes a validated lead',
  },
  {
    title: 'Lead Reporting',
    description:
      'Vehicles viewed, shopper activities, lead forms completed, Miles conversations',
  },
  {
    title: 'Unlimited Anytime Leads',
    description: 'One low monthly payment covers your lot & website 24/7',
  },
  {
    title: 'Association Discount',
    description: 'Locked in monthly discount for association members',
  },
  {
    title: 'Customer Ease of Use',
    description:
      'Simple QR scan or web visit provides enhanced shopper experience',
  },
  {
    title: 'Data Exports',
    description:
      'Export lead information along with crucial dealership data collected by system',
  },
  {
    title: 'Miles AI Agent',
    description:
      '24/7 agent to answer questions and prompt shoppers to provide contact info',
  },
];

const TabButton: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ position: 'relative', mt: { xs: 3, sm: 2 } }}>
    <Chip
      label={
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 700,
            fontSize: { xs: '12px', sm: '14px', md: '16px' },
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      }
      sx={{
        backgroundColor: '#2BBCD4',
        borderRadius: '8px',
        px: { xs: 0.5, sm: 1 },
        py: { xs: 1, sm: 1 },
        minWidth: 150,
        height: 'auto',
        cursor: 'default',
        '& .MuiChip-label': { pr: 1.5, pl: 1.5 },
      }}
    />

    {/* Avatar centered above the Chip */}
    <Avatar
      sx={{
        position: 'absolute',
        top: '-35%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 36,
        height: 36,
        border: '1px solid #e0e0e0',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CheckCircleIcon
        sx={{
          fontSize: 30, // ← slightly smaller than avatar = proper padding
          color: '#21b524',
          display: 'block', // ← prevents inline baseline shift
        }}
      />
    </Avatar>
  </Box>
);

const FeatureItem: React.FC<{ feature: Feature }> = ({ feature }) => (
  <Stack direction='row' spacing={1.5} alignItems='flex-start'>
    <StarIcon
      sx={{
        color: '#2BBCD4',
        fontSize: { xs: '18px', sm: '20px' },
        mt: '2px',
        flexShrink: 0,
      }}
    />
    <Typography
      variant='body2'
      sx={{
        color: '#333',
        lineHeight: 1.6,
        fontSize: { xs: '13px', sm: '15px' },
      }}
    >
      <Box component='span' sx={{ fontWeight: 700 }}>
        {feature.title}
      </Box>
      {' - '}
      {feature.description}
    </Typography>
  </Stack>
);

interface IProps {
  toggle: boolean;
  onClose: () => void;
  plan: PricingPlan;
  onGetStartedClicked: (code: string) => void;
}

const QRBuddySubscriptionDialog: React.FC<IProps> = ({
  toggle,
  onClose,
  plan,
  onGetStartedClicked,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    '',
    'Choose Program',
    'Account Setup',
    'Accept Terms',
    'Final Checkout',
    '',
  ];
  const activeStep = 1;

  const setupFeatures = [
    'Custom Dealer Branding',
    'Salesperson Setup',
    'Inventory Setup',
    'Custom Dealer Offers',
  ];

  return (
    <Dialog
      open={toggle}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: {
          borderRadius: isSmallScreen ? 0 : 3,
          maxHeight: '95vh',
        },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          //   p: { xs: 2, sm: 3 },
          //   borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(135deg, #f8fbff 0%, #fdf8ff 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component={'img'}
            src='/static/images/one_line_logo.png'
            sx={{ height: {xs: '60px' , sm: '80px', md: '100px'}, width: 'auto' }}
          />
        </Box>
        <IconButton onClick={onClose} aria-label='close'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #f8fbff 0%, #fdf8ff 100%)',
        }}
      >
        {/* Stepper */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel={true}
            orientation={'horizontal'}
            sx={{
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          >
            {steps.map((label, index) => (
              <Step key={`step-${index}`} completed={index <= activeStep}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: { xs: 28, sm: 32 },
                        height: { xs: 28, sm: 32 },
                        borderRadius: '50%',
                        // bgcolor:
                        //   index === activeStep
                        //     ? '#42a5f5'
                        //     : index < activeStep
                        //     ? '#4caf50'
                        //     : '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {index === 0 ||
                      index === steps.length - 1 ? null : index ===
                        activeStep ? (
                        <CheckCircleIcon
                          sx={{
                            fontSize: { xs: 20, sm: 32 },
                            color: '#42a5f5',
                          }}
                        />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{
                            fontSize: { xs: 20, sm: 32 },
                            color: '#42a5f5',
                          }}
                        />
                      )}
                    </Box>
                  )}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Main Content */}
        <Card
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                color: '#333',
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.75rem' },
              }}
            >
              {plan.details.title}
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#666',
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '1.1rem' },
              }}
            >
              {plan.details.description}
            </Typography>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* One-Time Setup Fee Card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 500,
                        color: '#333',
                        mb: 1,
                        fontSize: { xs: '0.938rem', sm: '1.125rem' },
                        '& span': {
                          color: '#42a5f5',
                        },
                      }}
                    >
                      One-Time Setup Fee <span>— Per Location</span>
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'baseline', mb: 2.5 }}
                    >
                      {plan.code === 'usub' ? (
                        <>
                          <CrossedText text={'$2,000'} />

                          <Box
                            sx={{
                              border: '2px solid #239FCE',
                              px: 2,
                              py: 1,
                              borderRadius: '40px',
                              position: 'relative',
                              mb: 3,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 700,
                                color: '#333',
                                fontSize: { xs: '1.50rem', sm: '1.5rem' },
                                '& span': {
                                  // fontWeight: 400,
                                  fontSize: { xs: '0.813rem', sm: '1rem' },
                                },
                              }}
                            >
                              $0 <span>Limited Time Promotion - Act NOW!</span>
                            </Typography>

                            {/* ✅ Anchored to the bottom border, centered */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                transform: 'translate(-50%, 105%)',
                                bgcolor: '#00BE62',
                                borderRadius: '40px',
                                px: 1.5,
                                py: 0.4,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: 'white',
                                  fontSize: { xs: '0.813rem', sm: '1rem' },
                                  fontWeight: 600,
                                }}
                              >
                                SETUP FEE WAIVED
                              </Typography>
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant='h3'
                            sx={{
                              fontWeight: 700,
                              color: '#333',
                              fontSize: { xs: '1.50rem', sm: '2rem' },
                            }}
                          >
                            $2,000
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              ml: 1,
                              color: '#333',
                              fontSize: { xs: '0.813rem', sm: '1rem' },
                            }}
                          >
                            Due Today
                          </Typography>
                        </>
                      )}
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                      {setupFeatures.map((feature, index) => (
                        <Box
                          key={`setup-feature-${index}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1.5,
                          }}
                        >
                          <StarIcon
                            sx={{
                              fontSize: { xs: 16, sm: 18 },
                              color: '#42a5f5',
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant='body2'
                            sx={{
                              color: '#333',
                              fontSize: { xs: '0.813rem', sm: '0.875rem' },
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Typography
                      variant='caption'
                      sx={{
                        color: '#666',
                        display: 'block',
                        mb: 2.5,
                        fontSize: { xs: '0.75rem', sm: '0.813rem' },
                      }}
                    >
                      This is a one-time activation fee collected at checkout.
                    </Typography>

                    <Box
                      sx={{
                        bgcolor: '#f5f5f5',
                        p: 2,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                          mb: 0.5,
                          fontSize: { xs: '0.813rem', sm: '0.875rem' },
                        }}
                      >
                        Due Today: { plan.code === 'usub' ? (<><CrossedText text='$2,000' fontSize={{ xs: '0.813rem', sm: '0.875rem' }} lineThickness={'2px'} /> $0</>) : (<>$2,000</>)}
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{
                          color: '#666',
                          fontSize: { xs: '0.75rem', sm: '0.813rem' },
                        }}
                      >
                        {plan.code === 'usub'
                          ? ''
                          : plan.code === 'sub'
                          ? 'Subscription and wallet fees billed separately in 5 business days.'
                          : plan.code === 'payl'
                          ? 'Wallet credit billed separately in 5 business days.'
                          : ''}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Subscription Service Card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',                      
                    }}>
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                          mb: 1,
                          fontSize: { xs: '0.938rem', sm: '1.125rem' },
                          '& span': {
                            fontWeight: 400,
                          },
                        }}
                      >
                        <HtmlRenderer htmlString={plan.details.subtitle} />
                      </Typography>
                      {plan.code === 'usub' && <Box
                        sx={{
                          border: '2px solid #239FCE',
                          px: 2,
                          py: 1,
                          borderRadius: '40px',                          
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 700,
                            color: '#333',
                            fontSize: { xs: '0.813rem', sm: '1rem' },                            
                          }}
                        >
                          30 DAY FREE TRIAL *
                        </Typography>
                      </Box>}
                    </Box>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        mb: 2.5,
                        borderBottom: '1px solid #e0e0e0',
                      }}
                    >
                      {plan.code === 'usub' ? (
                        <>
                          {/* <CrossedText text={'$2,000'} /> */}
                          <Typography
                            variant='h3'
                            sx={{
                              fontWeight: 700,
                              color: '#333',
                              //   fontSize: { xs: '1.25rem', sm: '1.5rem' },
                              fontSize: { xs: '1.50rem', sm: '2rem' },
                              '& span': {
                                fontWeight: 400,
                                fontSize: { xs: '0.813rem', sm: '1rem' },
                              },
                            }}
                          >
                            &nbsp;$2,000 <span>Per month/Per Rooftop</span>
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant='h3'
                            sx={{
                              fontWeight: 700,
                              color: '#333',
                              fontSize: { xs: '1.50rem', sm: '2rem' },
                            }}
                          >
                            {plan.details.price}
                          </Typography>
                          {plan.details.priceDescription && (
                            <Typography
                              variant='body1'
                              sx={{
                                ml: 1,
                                color: '#333',
                                fontSize: { xs: '0.813rem', sm: '1rem' },
                              }}
                            >
                              {plan.details.priceDescription}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>

                    {plan.code === 'sub' && (
                      <Box
                        sx={{
                          background:
                            'linear-gradient(to right, #42a5f5 0, #fff 100%)',
                          borderRadius: 1.5,
                          mb: 2,
                          p: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: { xs: '0.813rem', sm: '1.2rem' },
                          }}
                        >
                          90-Day Agreement
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 2.5 }}>
                      {plan.details.features.map((feature, index) => (
                        <Box
                          key={`plan-feature-${index}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1.5,
                          }}
                        >
                          <StarIcon
                            sx={{
                              fontSize: { xs: 16, sm: 18 },
                              color: '#42a5f5',
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant='body2'
                            sx={{
                              color: '#555',
                              fontSize: { xs: '0.813rem', sm: '0.875rem' },
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Alert
                      icon={<InfoIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                      severity='warning'
                      sx={{
                        mb: 2.5,
                        borderRadius: 2,
                        border: '1px solid #ebebeb',
                        '& .MuiAlert-message': {
                          fontSize: { xs: '0.75rem', sm: '0.813rem' },
                        },
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: { xs: '0.813rem', sm: '0.875rem' },
                        }}
                      >
                        { plan.code === 'usub' ? 'Subscription Charge Scheduled 7 days after the  completion of 30 day free trial' : <>{plan.details.noteTitle}{' '}
                        <Typography
                          component={'span'}
                          variant='caption'
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.813rem' } }}
                        >
                          {plan.details.noteDescription}
                        </Typography>
                        </>}
                      </Typography>
                    </Alert>

                    <Button
                      variant='contained'
                      fullWidth
                      onClick={() => {
                        onGetStartedClicked(plan.code);
                        onClose();
                      }}
                      sx={{
                        bgcolor: '#4caf50',
                        color: 'white',
                        py: { xs: 1.5, sm: 1.75 },
                        fontSize: { xs: '0.938rem', sm: '1.063rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        mb: 1,
                        '&:hover': {
                          bgcolor: '#45a049',
                        },
                      }}
                    >
                      Start Checkout
                    </Button>
                    <Button
                      variant='text'
                      fullWidth
                      onClick={onClose}
                      sx={{
                        color: '#42a5f5',
                        textTransform: 'none',
                        fontSize: { xs: '0.813rem', sm: '0.875rem' },
                      }}
                    >
                      Cancel & return to pricing
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {plan.code === 'usub' && <Box
          sx={{
            // minHeight: "100vh",
            // backgroundColor: "#f0f4f8",
            py: { xs: 4, sm: 6 },
          }}
        >
          {/* ── Title ── */}
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            align='center'
            sx={{
              fontWeight: 500,
              color: '#1a1a2e',
              mb: { xs: 3, sm: 4 },
              letterSpacing: '-0.5px',
            }}
          >
            QR Buddy{' '}
            <Box component='span' sx={{ color: '#2BBCD4', fontWeight: 700 }}>
              Unlimited
            </Box>
            <Box
              component='sup'
              sx={{
                fontSize: '12px',
                color: '#888',
                verticalAlign: 'super',
                ml: 0.5,
              }}
            >
              ™
            </Box>
          </Typography>

          {/* ── Tab Buttons ── */}
          <Stack
            direction='row'
            flexWrap='wrap'
            justifyContent='center'
            gap={{ xs: 2, sm: 1.5 }}
            sx={{ mb: { xs: 3, sm: 4 } }}
          >
            {tabs.map((tab) => (
              <TabButton key={tab.label} label={tab.label} />
            ))}
          </Stack>

          {/* ── Features Card ── */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid #e0e7ef',
              bgcolor: '#f9f9f8',
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                // backgroundColor: "#fff",
                borderBottom: '1px solid #e8edf3',
                py: { xs: 1, sm: 2 },
                px: { xs: 2, sm: 3 },
              }}
            >
              <Typography
                variant='h6'
                align='center'
                sx={{
                  fontWeight: 700,
                  color: '#1a1a2e',
                  fontSize: { xs: '16px', sm: '18px' },
                }}
              >
                What's Included
              </Typography>
            </Box>

            {/* Features Grid */}
            <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2.5, sm: 3 } }}>
              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                {/* Left Column */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={{ xs: 1.5, sm: 2 }}>
                    {featuresLeft.map((feature) => (
                      <FeatureItem key={feature.title} feature={feature} />
                    ))}
                  </Stack>
                </Grid>

                {/* Right Column */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={{ xs: 1.5, sm: 2 }}>
                    {featuresRight.map((feature) => (
                      <FeatureItem key={feature.title} feature={feature} />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>}
      </DialogContent>
    </Dialog>
  );
};

export default QRBuddySubscriptionDialog;
